/**
 * Run History Orbs
 *
 * Displays a row of colored circles representing recent agent schedule runs.
 * Inspired by TranscriptVersionOrbs but adapted for run status visualization.
 *
 * Colors:
 *  - Green  = completed successfully
 *  - Red    = failed (error)
 *  - Yellow = completed with warnings
 *  - Grey   = session expired (cleaned up)
 *  - Pulsing blue = currently running
 */

import React, { useState } from 'react';
import type { RunEntry, RunStatus, TriggerType } from '../services/agentScheduleService';

interface RunHistoryOrbsProps {
  runs: RunEntry[];
  maxVisible?: number;
  onClickRun?: (run: RunEntry) => void;
}

function statusColor(status: RunStatus): string {
  switch (status) {
    case 'success':
      return 'bg-[#FFB52B] dark:bg-[#FFB52B]';
    case 'failure':
    case 'timeout':
      return 'bg-red-500 dark:bg-red-400';
    case 'skipped':
      return 'bg-yellow-500/60 dark:bg-yellow-500/60';
    case 'running':
    case 'pending':
      return 'bg-[#FFB52B] dark:bg-[#FFB52B]';
    default:
      return 'bg-gray-400 dark:bg-gray-500';
  }
}

function statusRing(status: RunStatus): string {
  switch (status) {
    case 'success':
      return 'ring-[#FFB52B]/40 dark:ring-[#FFB52B]/30';
    case 'failure':
    case 'timeout':
      return 'ring-red-300 dark:ring-red-600';
    case 'skipped':
      return 'ring-yellow-300 dark:ring-yellow-600';
    case 'running':
    case 'pending':
      return 'ring-[#FFB52B]/40 dark:ring-[#FFB52B]/30';
    default:
      return 'ring-gray-300 dark:ring-gray-600';
  }
}

function triggerLabel(type: TriggerType | null): string {
  switch (type) {
    case 'cron': return 'Cron';
    case 'event': return 'Event';
    case 'manual': return 'Manual';
    case 'dependency': return 'Dependency';
    default: return 'Unknown';
  }
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '--';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
}

/** Parse a date string that may be SQLite format (space-separated, no T/Z) */
function parseSqliteDate(dateStr: string): Date {
  let normalized = dateStr;
  if (normalized.includes(' ') && !normalized.includes('T')) {
    normalized = normalized.replace(' ', 'T') + 'Z';
  }
  return new Date(normalized);
}

function timeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return 'unknown';
  try {
    const date = parseSqliteDate(dateStr);
    if (isNaN(date.getTime())) return 'unknown';
    const diffMs = Date.now() - date.getTime();
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 0) return 'in the future';
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    if (hours < 48) return 'yesterday';
    const days = Math.floor(hours / 24);
    if (days < 14) return `${days} days ago`;
    const weeks = Math.floor(days / 7);
    if (days < 60) return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return 'unknown';
  }
}

function exactDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  try {
    const date = parseSqliteDate(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleString();
  } catch {
    return '';
  }
}

const RunHistoryOrbs: React.FC<RunHistoryOrbsProps> = ({
  runs: rawRuns,
  maxVisible = 8,
  onClickRun,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const runs = Array.isArray(rawRuns) ? rawRuns : [];
  const safeMaxVisible = Math.max(1, Math.min(maxVisible, 12));

  if (runs.length === 0) {
    return (
      <div className="flex items-center gap-1">
        <span className="text-xs text-gray-400 dark:text-gray-500 italic">No runs yet</span>
      </div>
    );
  }

  // Show latest runs (most recent on right), limited to maxVisible
  const visibleRuns = runs.slice(0, safeMaxVisible).reverse();

  return (
    <div className="flex min-w-0 items-center gap-1.5 sm:gap-1 relative">
      {visibleRuns.map((run, idx) => {
        const isRunning = run.status === 'running' || run.status === 'pending';
        const globalIdx = visibleRuns.length - 1 - idx; // For tooltip positioning
        const isHovered = hoveredIndex === idx;

        return (
          <div
            key={run.id || idx}
            className="relative flex-shrink-0"
            onMouseEnter={() => setHoveredIndex(idx)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <button
              type="button"
              onClick={() => onClickRun?.(run)}
              className={`
                w-5 h-5 sm:w-3 sm:h-3 rounded-full transition-all duration-200
                ${statusColor(run.status)}
                ${isHovered ? `ring-2 ${statusRing(run.status)} scale-125` : ''}
                ${isRunning ? 'animate-pulse' : ''}
                hover:scale-125 cursor-pointer
              `}
              aria-label={`Run ${run.status || 'unknown'}: ${timeAgo(run.createdAt)}`}
            />

            {/* Tooltip */}
            {isHovered && (
              <div
                className={`
                  absolute bottom-full mb-2 px-2.5 py-1.5
                  bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-xl
                  pointer-events-none whitespace-nowrap z-50
                  ${globalIdx < 3 ? 'left-0' : 'left-1/2 transform -translate-x-1/2'}
                `}
              >
                <div className="font-medium capitalize">{run.status}</div>
                <div className="text-gray-300 dark:text-gray-400">
                  {timeAgo(run.createdAt)}
                </div>
                {exactDateTime(run.createdAt) && (
                  <div className="text-gray-400 dark:text-gray-500 text-[10px]">
                    {exactDateTime(run.createdAt)}
                  </div>
                )}
                <div className="text-gray-300 dark:text-gray-400">
                  {triggerLabel(run.triggerType ?? null)} | {formatDuration(run.durationSeconds)}
                </div>
                {run.totalCost != null && run.totalCost > 0 && (
                  <div className="text-gray-400">${Number(run.totalCost).toFixed(3)}</div>
                )}
                {run.errorMessage && (
                  <div className="text-red-300 max-w-[200px] truncate mt-0.5">
                    {run.errorMessage}
                  </div>
                )}
                <div
                  className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0
                    border-l-4 border-r-4 border-t-4
                    border-l-transparent border-r-transparent
                    border-t-gray-900 dark:border-t-gray-700`}
                />
              </div>
            )}
          </div>
        );
      })}
      {runs.length > safeMaxVisible && (
        <span className="ml-0.5 flex-shrink-0 text-xs text-gray-400 dark:text-gray-500">
          +{runs.length - safeMaxVisible}
        </span>
      )}
    </div>
  );
};

export default RunHistoryOrbs;
